import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { payrollApi } from "../../services/payrollApi";
import { paymentEvidenceApi } from "../../services/paymentEvidenceApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { NotFoundState } from "../../components/NotFoundState";
import { PayrollLifecycleStepper } from "../../components/payroll/PayrollLifecycleStepper";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function PayrollPeriodDetailPage() {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canPreparePayroll, canReviewPayroll, canPublishPayroll } = usePermissions();
  const [period, setPeriod] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lines, setLines] = useState([]);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);

  const load = useCallback(() => {
    payrollApi.getPeriod(periodId).then((r) => { setPeriod(r.data); setNotFound(false); }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
    payrollApi.listLines(periodId).then((r) => setLines(r.data)).catch(() => setLines([]));
    paymentEvidenceApi.listPeriodPayments(periodId).then((r) => {
      const withLine = r.data.filter((row) => row.status !== "NOT_READY");
      setPaymentsSummary({ total: withLine.length, confirmed: withLine.filter((row) => row.status === "PAYMENT_CONFIRMED").length });
    }).catch(() => {});
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  if (notFound) return <NotFoundState testId="payroll-period-not-found" backTo="/management/payroll" />;
  if (!period) return <Skeleton className="h-64 w-full rounded-xl" data-testid="payroll-period-loading" />;

  const run = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const blockedCount = (period.readiness_report || []).filter((r) => !r.ready).length;
  const affectedEmployees = lines.map((l) => l.employee_name).join(", ") || "-";

  const totalGross = lines.reduce((sum, l) => sum + (l.status !== "BLOCKED" ? Number(l.gross_pay || 0) : 0), 0);

  return (
    <div className="space-y-5" data-testid="payroll-period-detail-page">
      <PageHelpBanner pageKey="payroll-period-detail" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtPayroll.periodDetailHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{period.payroll_month} {t("mgmtPayroll.payrollPeriod")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtPayroll.paymentDate")}: {period.payment_date}</p>
        </div>
        <StatusBadge status={period.status} />
      </div>

      <PayrollLifecycleStepper period={period} paymentsSummary={paymentsSummary} testId="payroll-lifecycle-stepper" />

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-2" data-testid="period-actions">
        {canPreparePayroll && (
          <>
            <Button variant="outline" size="sm" data-testid="readiness-check-btn" disabled={busy} onClick={() => run(() => payrollApi.runReadinessCheck(periodId), t("mgmtPayroll.readinessCheckComplete"))}>{t("mgmtPayroll.runReadinessCheck")}</Button>
            <Button size="sm" data-testid="calculate-btn" disabled={busy || !period.readiness_report} onClick={() => run(() => payrollApi.calculatePeriod(periodId), t("mgmtPayroll.calculationComplete"))}>{t("mgmtPayroll.calculate")}</Button>
            <Button variant="outline" size="sm" data-testid="submit-for-review-btn" disabled={busy || period.status !== "CALCULATED"} onClick={() => run(() => payrollApi.submitForReview(periodId), t("mgmtScheduling.submittedForApproval"))}>{t("mgmtPayroll.submitForReview")}</Button>
          </>
        )}
        {canReviewPayroll && period.status === "SUBMITTED_FOR_REVIEW" && (
          <>
            <Button size="sm" data-testid="approve-period-btn" disabled={busy} onClick={() => run(() => payrollApi.approvePeriod(periodId), t("mgmtPayroll.periodApproved"))} className="bg-emerald-600 hover:bg-emerald-700">{t("common.approve")}</Button>
            <Button variant="outline" size="sm" data-testid="return-for-correction-btn" disabled={busy} onClick={() => setReturnDialogOpen(true)} className="text-red-600 border-red-200 hover:bg-red-50">{t("mgmtPayroll.returnForCorrection")}</Button>
          </>
        )}
        {canPublishPayroll && period.status === "REVIEWED_APPROVED" && (
          <Button size="sm" data-testid="publish-period-btn" disabled={busy} onClick={() => run(() => payrollApi.publishPeriod(periodId), t("mgmtPayroll.periodPublished"))} className="bg-emerald-600 hover:bg-emerald-700">{t("mgmtPayroll.publish")}</Button>
        )}
        {canPublishPayroll && period.status === "PUBLISHED" && (
          <>
            <Button variant="outline" size="sm" data-testid="generate-reports-btn" disabled={busy} onClick={() => run(() => payrollApi.generateReports(periodId), t("mgmtPayroll.reportsGenerated"))}>{t("mgmtPayroll.generatePdfReports")}</Button>
            {period.combined_report_path && (
              <a data-testid="download-combined-report-link" href={payrollApi.getCombinedReportUrl(periodId)} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 inline-flex items-center">{t("mgmtPayroll.downloadCombinedZip")}</a>
            )}
          </>
        )}
        {(period.status === "REVIEWED_APPROVED" || period.status === "PUBLISHED") && (
          <Button variant="outline" size="sm" data-testid="open-payments-btn" onClick={() => navigate(`/management/payroll/periods/${periodId}/payments`)}>{t("mgmtPayroll.paymentEvidence")}</Button>
        )}
        {period.status === "PUBLISHED" && (
          <Button variant="outline" size="sm" data-testid="open-admin-package-btn" onClick={() => navigate(`/management/payroll/periods/${periodId}/admin-package`)}>{t("mgmtPayroll.adminPackage")}</Button>
        )}
      </div>

      {period.readiness_report && (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="readiness-report-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">{t("mgmtPayroll.readinessReport")}</p>
          <div className="space-y-1">
            {period.readiness_report.map((r) => (
              <div key={r.employee_id} className="flex items-center justify-between text-sm border-b border-gray-50 py-1" data-testid={`readiness-row-${r.employee_id}`}>
                <span className="text-gray-700">{r.employee_name}</span>
                {r.ready ? <span className="text-emerald-600 text-xs font-semibold">{t("mgmtPayroll.ready")}</span> : <span className="text-red-600 text-xs font-semibold">{r.blockers.join(", ")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtScheduling.colStore")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colRegularHrs")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colLateNightHrs")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colGrossPay")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th></tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-gray-100 cursor-pointer hover:bg-gray-50" data-testid={`payroll-line-row-${l.id}`} onClick={() => navigate(`/management/payroll/lines/${l.id}?period=${periodId}`)}>
                <td className="px-4 py-2 font-semibold text-blue-600 hover:underline">{l.employee_name}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{l.store_location || "-"}</td>
                <td className="px-4 py-2 text-gray-600">{l.regular_hours || "-"}</td>
                <td className="px-4 py-2 text-gray-600">{l.late_night_hours || "-"}</td>
                <td className="px-4 py-2 text-gray-800 font-semibold">¥{l.gross_pay}</td>
                <td className="px-4 py-2"><StatusBadge status={l.status} /></td>
              </tr>
            ))}
            {lines.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-lines-row">{t("mgmtPayroll.noCalculatedLines")}</td></tr>}
          </tbody>
          {lines.length > 0 && (
            <tfoot><tr className="border-t-2 border-gray-200 bg-gray-50"><td className="px-4 py-2 font-bold" colSpan={4}>{t("mgmtPayroll.total")}</td><td className="px-4 py-2 font-bold" data-testid="period-total-gross">¥{totalGross.toLocaleString()}</td><td /></tr></tfoot>
          )}
        </table>
      </div>

      <ReasonDialog
        open={returnDialogOpen} onOpenChange={setReturnDialogOpen} title={t("mgmtPayroll.returnPayrollForCorrectionTitle")}
        contextItems={[
          { label: t("mgmtPayroll.payrollPeriod"), value: period.payroll_month },
          { label: t("mgmtPayroll.currentStage"), value: period.status.replaceAll("_", " ") },
          { label: t("mgmtPayroll.preparer"), value: period.prepared_by || "-" },
          { label: t("mgmtPayroll.reviewerYou"), value: period.reviewed_by || "-" },
          { label: t("mgmtPayroll.employeesAffected"), value: affectedEmployees },
          { label: t("mgmtPayroll.blockingIssues"), value: blockedCount > 0 ? `${blockedCount} ${t("mgmtPayroll.readinessBlockers")}` : t("mgmtPayroll.noneFlaggedAtReadiness") },
        ]}
        reasonLabel={t("mgmtPayroll.correctionReason")} danger confirmLabel={t("mgmtPayroll.returnForCorrection")}
        onConfirm={async (reason) => { setBusy(true); try { await payrollApi.returnForCorrection(periodId, reason); toast.success(t("mgmtPayroll.returnedForCorrection")); load(); } finally { setBusy(false); } }}
        testId="return-for-correction-dialog"
      />
    </div>
  );
}

